import { UserDefinedRow, AbsencePeriodUsecase, AbsenceCauseCode, CalculationRowType, CalculationRowSource } from "../../model";
import { UsecaseValidationMessage } from "../UsecaseValidationMessage";
import { BaseUsecaseLogic } from "../BaseUsecaseLogic";
import { HolidaysLogic } from "../../logic/HolidaysLogic";
import { UsecaseRow } from "../UsecaseRow";
import { Dates } from "../../util";
import { Translations } from "../../logic";

/** Client-side logic for Absence period as UserDefined row (in calculation).  */
export class AbsencePeriodUsecaseLogic extends BaseUsecaseLogic {

  /**
   * Updates the usecase values based on user input without going to the server.
   * @param row Row to update.
   */
  public updateUsecase(row: UsecaseRow<CalculationRowType.AbsencePeriod, AbsencePeriodUsecase>): void {
    const data: AbsencePeriodUsecase = row.data ?? {};
    data.kind = data.kind || AbsenceCauseCode.Undefined;
    this.updateIsPaid(row);
  }

  /** Gets validation message for AbsencePeriodUsecaseLogic  */
  public getUcValidation(row: UserDefinedRow): UsecaseValidationMessage {
    const data: AbsencePeriodUsecase = row.data ?? {};
    data.kind = data.kind ?? AbsenceCauseCode.Undefined;
    if (data.kind === AbsenceCauseCode.Undefined &&
        row.source !== CalculationRowSource.PriceEmployment &&
        row.source !== CalculationRowSource.PriceCompany ) {
      return {
        msg: Translations.get("SALAXY.VALIDATION.Usecases.noKind"),
        type: "error"
      };
    }

    return {
      type: "default",
      msg: undefined,
    };
  }
  /**
   * Updates the IsHolidayAccrual
   * @param row Current row that is being edited.
   */
  public updateIsHolidayAccrual(row: UsecaseRow<CalculationRowType.AbsencePeriod, AbsencePeriodUsecase>): void {
    row.data.isHolidayAccrual = row.data.isPaid;
  }

  /**
   * Returns true if the select component should be shown for absence isPaid.
   * @param row Current row that is being edited.
   */
  public canSelectIsPaid(row: UsecaseRow<CalculationRowType.AbsencePeriod, AbsencePeriodUsecase>): boolean {
    if (!row.data.kind) {
      return false;
    }
    const type = HolidaysLogic.getSalaryPayableType(row.data.kind);
    return type === "defaultPaid" || type === "defaultUnpaid";
  }
  /**
   * Updates the IsPaid according to absence type.
   * @param row Current row that is being edited.
   * @param isKindChange Indicates if the change is due to kind change.
   */
  public updateIsPaid(row: UsecaseRow<CalculationRowType.AbsencePeriod, AbsencePeriodUsecase>, isKindChange = false): void {
    if (isKindChange) {
      // reset logic
      row.data.isPaid = null;
    }
    const type = HolidaysLogic.getSalaryPayableType(row.data.kind);
    if (row.data.isPaid == null) {
      row.data.isPaid = (type == "paid" || type == "defaultPaid");
      this.updateIsHolidayAccrual(row);
    }
    else if (row.data.isPaid == true && type == "unpaid") {
      row.data.isPaid = false;
      this.updateIsHolidayAccrual(row);
    }
    else if (row.data.isPaid == false && type == "paid") {
      row.data.isPaid = true;
      this.updateIsHolidayAccrual(row);
    }
  }
  /**
   * Updates the workdays count
   * @param row Current row that is being edited.
   */
  public updatePeriodDays(row: UsecaseRow<CalculationRowType.AbsencePeriod, AbsencePeriodUsecase>): void {
    if (!row.period) {
      return;
    }
    if (row.period.start && row.period.end && row.period.start > row.period.end) {
      row.period.end = row.period.start;
    }
    if (row.period.start && row.period.end) {
      if (row.period.days) {
        row.period.days = Dates.getWorkdays(row.period.start, row.period.end);
        row.period.daysCount = row.period.days.length;
      } else {
        row.period.daysCount = Dates.getWorkdays(row.period.start, row.period.end).length;
      }
    }
    row.count = row.period.daysCount || 0;
  }

  /**
   * Sets the days array in period to enable days selection UI.
   * @param row Current row that is being edited.
   */
  public setDaysSelection(row: UsecaseRow<CalculationRowType.AbsencePeriod, AbsencePeriodUsecase>): void {
    row.period ??= {};
    row.period.days = Dates.getWorkdays(row.period?.start ?? "", row.period?.end ?? "");
    row.period.daysCount = row.period.days.length;
    row.count = row.period.daysCount ?? 0;
  }


}
